import React from 'react'
import { useIntl } from 'react-intl'

import { BasicListItem } from './basic-list-item.component'

/**
 * @typedef {object} FieldConfig
 * @property {string} key - La clave para acceder al valor correspondiente en el objeto `data`.
 * @property {string} label - El texto que se mostrará como etiqueta para el elemento de la lista.
 * @property {'combo' | 'date' | 'currency' | 'file'} [type] - El tipo de dato para aplicar un formato especial.
 * @property {string} [comboId] - El ID para buscar las opciones correspondientes en el objeto `combos`. Requerido si `type` es 'combo'.
 * @property {boolean} [multiple] - Indica si el campo 'combo' puede tener múltiples valores. Se usa solo cuando `type` es 'combo'.
 * @property {string} [currency] - El código de moneda ISO 4217 (ej. 'USD', 'EUR'). Requerido si `type` es 'currency'.
 */

/**
 * @typedef {object} ComboOption
 * @property {string | number} key - El identificador único de la opción.
 * @property {string} value - El valor legible por humanos que se mostrará para la opción.
 */

/**
 * @typedef {object} Combos
 * @property {Object.<string, { data: ComboOption[] }>} - Un objeto donde cada clave es un `comboId` y su valor contiene un array de objetos `ComboOption`.
 */

/**
 * @typedef {object} BasicListProps
 * @property {object} data - El objeto que contiene los datos a mostrar. Sus claves deben corresponder a la propiedad `key` de los objetos en `fields`.
 * @property {FieldConfig[]} fields - Un array de objetos de configuración que define cada elemento de la lista, incluyendo su etiqueta, clave de datos y tipo de formato.
 * @property {Combos} combos - Un objeto que proporciona los datos para los campos de tipo 'combo', permitiendo la traducción de claves a valores legibles.
 */

/**
 * Un componente que renderiza una lista de elementos clave-valor a partir de un objeto de datos y una configuración.
 *
 * Itera sobre una configuración de `fields` para mostrar dinámicamente una lista de componentes `BasicListItem`.
 * Utiliza `react-intl` para formatear valores como fechas y monedas, y es capaz de resolver
 * identificadores de campos de tipo 'combo' a sus representaciones textuales. Los elementos para los cuales
 * no existe un valor en el objeto `data` no se renderizan.
 *
 * @component
 * @example
 * const itemData = {
 * status: 'active',
 * categories: ['cat1', 'cat3'],
 * createdAt: '2025-10-20T12:30:00Z',
 * price: 99.95,
 * attachment: new File(["content"], "factura.pdf"),
 * };
 *
 * const itemFields = [
 * { label: 'Estado', key: 'status', type: 'combo', comboId: 'statusOptions' },
 * { label: 'Categorías', key: 'categories', type: 'combo', comboId: 'categoryOptions', multiple: true },
 * { label: 'Fecha de Creación', key: 'createdAt', type: 'date' },
 * { label: 'Precio', key: 'price', type: 'currency', currency: 'EUR' },
 * { label: 'Adjunto', key: 'attachment', type: 'file' },
 * ];
 *
 * const comboData = {
 * statusOptions: {
 * data: [
 * { key: 'active', value: 'Activo' },
 * { key: 'pending', value: 'Pendiente' },
 * ],
 * },
 * categoryOptions: {
 * data: [
 * { key: 'cat1', value: 'Electrónica' },
 * { key: 'cat2', value: 'Hogar' },
 * { key: 'cat3', value: 'Deportes' },
 * ],
 * },
 * };
 *
 * // Este componente debe estar envuelto en un <IntlProvider> de react-intl.
 * <BasicList data={itemData} fields={itemFields} combos={comboData} />
 *
 * @param {BasicListProps} props - Las props del componente BasicList.
 */
export const BasicList = ({ data, fields, combos }) => {
  const { formatDate, formatNumber } = useIntl()

  const getValueByType = (value, field, combos) => {
    switch (field.type) {
      case 'combo': {
        if (field.comboId && combos[field.comboId]?.data) {
          if (field.multiple) {
            let selectedOptionsValues = []
            value.forEach((key) => {
              const selectedOption = combos[field.comboId].data.find((option) => option.key === key)
              if (selectedOption) selectedOptionsValues.push(selectedOption.value)
            })
            return selectedOptionsValues.join(', ')
          } else {
            const selectedOption = combos[field.comboId].data.find((option) => option.key === value)
            if (selectedOption) {
              return selectedOption.value
            }
          }
        }
        break
      }
      case 'date': {
        return formatDate(new Date(value))
      }
      case 'currency': {
        return formatNumber(value, { style: 'currency', currency: field.currency })
      }
      case 'file': {
        return value.name
      }
      default:
        return value
    }
  }

  return fields.map((field) => {
    const value = data[field.key]
    return value ? (
      <BasicListItem
        {...{ key: field.key, label: field.label, value: getValueByType(value, field, combos) }}
      />
    ) : null
  })
}
